/**
 * Switches scheduled releases from Coming Soon to Buy Now at the release moment for visitors who
 * already have the page open. The server decides the state on each page load
 * (snippets/unit-release-state.liquid); this only covers the time after the page was rendered.
 *
 * Markup: an element with data-unit-release="<unix seconds>" and data-unit-release-mode:
 * - "reload": reload the page, so the product form renders live with Buy Now and Shop Pay.
 * - "swap":   hide [data-release-before] children and show [data-release-after] ones (cards, links).
 */
(() => {
  const RELOAD_PARAM = 'released';
  const MAX_RELOADS = 5;
  const RETRY_MS = 15000;
  // setTimeout overflows above ~24.8 days; anything further out is handled on a later page load.
  const MAX_DELAY_MS = 2 ** 31 - 1;

  function reload() {
    const url = new URL(window.location.href);
    const attempt = Number(url.searchParams.get(RELOAD_PARAM) || 0) + 1;
    if (attempt > MAX_RELOADS) return;
    // A fresh query string skips any cached copy of the pre-release page.
    url.searchParams.set(RELOAD_PARAM, String(attempt));
    window.location.replace(url.toString());
  }

  function swap(element) {
    element.querySelectorAll('[data-release-before]').forEach((node) => (node.hidden = true));
    element.querySelectorAll('[data-release-after]').forEach((node) => (node.hidden = false));
    element.removeAttribute('data-unit-release');
  }

  function release(element) {
    if (element.dataset.unitReleaseMode === 'reload') {
      reload();
    } else {
      swap(element);
    }
  }

  function schedule(element) {
    const releaseAt = Number(element.dataset.unitRelease) * 1000;
    if (!releaseAt) return;

    const delay = releaseAt - Date.now();
    if (delay <= 0) {
      // The server still rendered this as unreleased: either a cached page or the visitor's clock
      // runs ahead. Reloads are capped, so a fast clock can't cause a reload loop.
      const reloaded = new URL(window.location.href).searchParams.has(RELOAD_PARAM);
      if (element.dataset.unitReleaseMode === 'reload' && reloaded) {
        window.setTimeout(reload, RETRY_MS);
      } else {
        release(element);
      }
      return;
    }

    if (delay <= MAX_DELAY_MS) {
      // A second of slack so the server agrees the release time has passed.
      window.setTimeout(() => release(element), delay + 1000);
    }
  }

  // Countdown text next to a scheduled release: "3d 4h 12m", then "4h 12m 05s" on the last day.
  function formatLeft(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`;
  }

  function startCountdowns() {
    const nodes = [...document.querySelectorAll('[data-unit-countdown]')];
    if (!nodes.length) return;
    const tick = () => {
      for (const node of nodes) {
        const left = Number(node.dataset.unitCountdown) * 1000 - Date.now();
        node.hidden = left <= 0;
        if (left > 0) node.textContent = formatLeft(left);
      }
    };
    tick();
    window.setInterval(tick, 1000);
  }

  function init() {
    document.querySelectorAll('[data-unit-release]').forEach(schedule);
    startCountdowns();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
